import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useId, useRef, useState } from 'react';
import __isEmpty from 'lodash/isEmpty';
import __isUndefined from 'lodash/isUndefined';
import { useUser } from 'hooks/useUser';
import { useModal } from 'hooks/3catinfo/useModal';
import { useModulAvis } from 'hooks/useModulAvis';
import { getStorageValue, setStorageValue } from '@portal-internet/core';
import { trescatinfoComponents } from 'components/3catinfo';
import Imatge from 'components/3catinfo/Imatge';
import Boto from 'components/3catinfo/Boto';
import Icona from '../../ui/Icona';
import Text from 'components/3catinfo/Text';
import styles from './onboardingBase.module.scss';

export default function Component(props) {
    const { onboardingActiu } = props;
    const { component, componentProps } = onboardingActiu;

    const { setModal, removeModal } = useModal();
    const { isModulAvisActive } = useModulAvis();
    const router = useRouter();
    const { signUp, signIn, loginState, loginStateTypes, isAuthenticated } = useUser();
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showedToday, setShowedToday] = useState(true);
    const [cookieChecked, setCookieChecked] = useState(false);
    const [showedOnboarding, setShowedOnboarding] = useState(true);
    const timeoutShowOnboarding = useRef();
    const refOnboarding = useRef();
    const isMountedRef = useRef(true);

    const usuarisLogats = onboardingActiu?.usuarisLogats || false;
    const usuarisNoLogats = onboardingActiu?.usuarisNoLogats || false;
    const ComponentOnboarding = trescatinfoComponents[component]?.dynamicComponent;
    const isTest =
        typeof window !== 'undefined' && window.Cypress && window?.Cypress?.spec?.baseName !== 'Onboarding.test.feature';
    const isEmbedOrPopup = router.asPath.includes('/popup/') || router.asPath.includes('/embed/');
    const isRedl = router?.query?.editmode === 'true' || router?.query?.editmode === 'yes';
    const nomCookie = `onboarding_3catinfo_${onboardingActiu?.id}`;
    const estructuraCookieDefault = { lastShown: null, count: 0 };
    const SHOW_INTERVAL_HOURS = onboardingActiu.horesCadaQuantEsMostra || 0;
    const MAX_ONBOARDING = onboardingActiu.numMaxCopsQueEsMostra ?? null;

    const saveOnStorage = (values) => {
        setStorageValue(nomCookie, values, {
            path: '/3catinfo',
            domain: '.3cat.cat',
            expires: 7
        });
    };
    const initializeValues = (data) => (__isUndefined(data) ? estructuraCookieDefault : data);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearTimeout(timeoutShowOnboarding.current);
        };
    }, []);

    useEffect(() => {
        const fetchCookieData = async () => {
            const data = await getStorageValue(nomCookie);
            if (!isMountedRef.current) return;
            const value = initializeValues(data);
            const { canShow } = getOnboardingStatus(value);
            setShowedOnboarding(!canShow);
            setCookieChecked(true);
        };
        fetchCookieData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tancaOnboarding = () => {
        removeModal();
        netejeUrlOnboarding();
        setShowOnboarding(false);
        setShowedToday(true);
    };

    const netejeUrlOnboarding = () => {
        let url = router.asPath?.split('#')[0];
        router.push(url, undefined, { scroll: false });
    };

    useEffect(() => {
        if (!__isUndefined(refOnboarding.current) && showOnboarding === true && !isTest && !showedToday) {
            refOnboarding.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refOnboarding.current]);

    const getOnboardingStatus = (value) => {
        const hasPassedInterval =
            !value.lastShown || Date.now() - new Date(value.lastShown).getTime() >= SHOW_INTERVAL_HOURS * 60 * 60 * 1000;

        const effectiveCount = hasPassedInterval ? 0 : value.count;
        const canShow = MAX_ONBOARDING === null || effectiveCount < MAX_ONBOARDING;

        return { canShow, effectiveCount };
    };

    const mostrarOnboarding = (userLoginState) => {
        if (isAuthenticated === true) return usuarisLogats;
        if (isAuthenticated === false) return usuarisNoLogats;

        // Fallback while auth status is still being resolved.
        return userLoginState === loginStateTypes.GUEST ? usuarisNoLogats : usuarisLogats;
    };

    useEffect(() => {
        if (isRedl) return;
        if (cookieChecked && !showedOnboarding && mostrarOnboarding(loginState)) {
            getStorageValue(nomCookie).then((data) => {
                const value = initializeValues(data);
                const { canShow, effectiveCount } = getOnboardingStatus(value);
                if (!canShow) return;

                timeoutShowOnboarding.current = setTimeout(() => {
                    if (isMountedRef.current) {
                        setShowOnboarding(true);
                        value.lastShown = new Date().toISOString();
                        value.count = effectiveCount + 1;
                        saveOnStorage(value);
                    }
                }, 3500);
            });
        } else if (!mostrarOnboarding(loginState)) {
            setShowOnboarding(false);
        }

        return () => clearTimeout(timeoutShowOnboarding.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginState, isAuthenticated, cookieChecked, showedOnboarding]);

    const actions = {
        signUp: () => signUp(),
        signIn: () => signIn(),
        close: () => tancaOnboarding()
    };

    const arrayEstils = onboardingActiu.estils?.split(' ') || [];
    const stylesOnboarding = clsx({
        [styles.onBoardingWrapper]: true,
        ...arrayEstils.reduce((acc, currentStyle) => {
            acc[styles[currentStyle]] = !__isEmpty(currentStyle);
            return acc;
        }, {})
    });

    const componentId = useId().replace(/:/g, '-');
    const containerId = `custom-container-onboarding-${componentId}`;
    const cssProcessat = onboardingActiu.css
        ? `${onboardingActiu.css.replace(/(^|[^}]+)\s*\{/g, (match) => `#${containerId} ${match.trim()}`)}`
        : '';

    const contentOnBoarding = () => {
        return (
            <div
                data-testid={`onboarding-base-${onboardingActiu.id}`}
                id={containerId}
                className={stylesOnboarding}
                {...{
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                {cssProcessat && <style>{cssProcessat}</style>}
                <div className={styles.mask + ' ' + styles.onboarding} onClick={() => tancaOnboarding()} ref={refOnboarding}>
                    <div
                        className={styles.wrapper}
                        onClick={(e) => e.stopPropagation()}
                        style={{ '--wrapper-width': onboardingActiu.width }}
                    >
                        <div className={styles.img}>
                            <div className={styles.imgTop}>
                                <Imatge
                                    src={onboardingActiu.imatge?.src}
                                    layout={onboardingActiu.imatge?.layout}
                                    objectFit={onboardingActiu.imatge?.objectFit}
                                    alt={onboardingActiu.imatge?.alt}
                                />
                            </div>
                        </div>
                        <div className={styles.text}>
                            <div className={styles.imgBottom}>
                                <Imatge
                                    src={onboardingActiu.imatgeMobile?.src}
                                    layout={onboardingActiu.imatgeMobile?.layout}
                                    objectFit={onboardingActiu.imatgeMobile?.objectFit}
                                    alt={onboardingActiu.imatgeMobile?.alt}
                                />
                            </div>
                            <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                <Icona icona='tanca' className={styles.ico_tanca} alt='Tanca' ariaHidden={true} />
                                <span className='sr-only'>Tanca finestra modal</span>
                            </button>

                            <div className={styles.contingutMobil}>
                                <Text tag='h1' className={styles.title} text={onboardingActiu.titol} idDom='onboarding-titol' />
                                <div className={styles.wrapperLogo}>
                                    <Imatge
                                        src={onboardingActiu.logo?.src}
                                        width={onboardingActiu.logo?.width}
                                        height={onboardingActiu.logo?.height}
                                        alt={onboardingActiu.logo?.alt}
                                    />
                                </div>
                                <Text tag='div' className={styles.subtitle} text={onboardingActiu.descripcio} />
                                <div className={styles.btns}>
                                    {onboardingActiu.botons?.map((boto, index) => (
                                        <Boto
                                            key={`boto-onboarding-${index}`}
                                            tag={boto.type === 'link' ? 'a' : 'button'}
                                            link={boto.link}
                                            text={boto.text}
                                            estil={boto.botoEstil}
                                            className={boto.estils
                                                ?.split(' ')
                                                .map((classe) => styles[classe] || classe)
                                                .join(' ')}
                                            onClick={boto.action ? actions[boto.action] : undefined}
                                            dataTestId={boto.dataTestId}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const contentOnBoardingComponent = () => {
        return (
            <div
                data-testid={`onboarding-base-${onboardingActiu.id}`}
                id={containerId}
                className={stylesOnboarding}
                {...{
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                {cssProcessat && <style>{cssProcessat}</style>}
                <div className={styles.mask + ' ' + styles.onboarding} onClick={() => tancaOnboarding()} ref={refOnboarding}>
                    <div
                        className={styles.wrapper}
                        onClick={(e) => e.stopPropagation()}
                        style={{ '--wrapper-width': onboardingActiu.width }}
                    >
                        <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                            <Icona
                                icona='tanca'
                                isBlanc={onboardingActiu?.icona_tanca?.color === 'blanc' || false}
                                className={styles.ico_tanca_blanc}
                                alt='Tanca'
                                ariaHidden={true}
                            />
                            <span className='sr-only'>Tanca finestra modal</span>
                        </button>
                        <ComponentOnboarding {...componentProps} />
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (!onboardingActiu) return;

        if (showOnboarding && !isTest && !isEmbedOrPopup && !isModulAvisActive) {
            setModal(
                onboardingActiu.tipus === 'component'
                    ? ComponentOnboarding
                        ? contentOnBoardingComponent()
                        : null
                    : contentOnBoarding()
            );
            return;
        }

        removeModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnboarding, isTest, isEmbedOrPopup, onboardingActiu, ComponentOnboarding, isModulAvisActive]);

    return null;
}
